// Navigation actions for the global hamburger menu (StudentNavMenu), mounted by
// AppLayout on every authenticated page. View-specific dialog actions (edit
// student, email, feedback) stay in useStudentNavActions — this composable only
// handles actions that change routes or are app-global (print, logout).
import { useRouter } from 'vue-router'
import { useSchoolStore } from '@/store/schoolStore'
import { pathWithSchool } from '@/utils/schoolRoutes'
import { PRINT_REQUEST_EVENT } from '@/utils/studentTablePrint'
import { areEvaluationsEnabled } from '@/utils/environment'

export function useGlobalNavActions() {
  const router = useRouter()
  const schoolStore = useSchoolStore()

  function pushSchoolPath(path, query = {}) {
    return router.push(pathWithSchool(path, schoolStore.currentSchoolId, query))
  }

  const navigateToAdmin = () => {
    pushSchoolPath('/admin', { tab: 'dashboard', category: 'dashboard' })
  }

  // Add Student / New Evaluation live on /students; route there with a query
  // flag the view watches to open its dialog (works whether or not we're
  // already on the page).
  const goToAddStudent = async () => {
    await schoolStore.loadSchools()
    pushSchoolPath('/students', { addStudent: '1' })
  }

  const goToAddEvaluation = async () => {
    await schoolStore.loadSchools()
    pushSchoolPath('/students', { addEvaluation: '1' })
  }

  const handleLogout = async () => {
    try {
      const { useAuthStore } = await import('@/store/authStore')
      const authStore = useAuthStore()
      await authStore.logout()
      router.push('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleNavAction = async (action, payload) => {
    switch (action) {
      case 'students':
        await schoolStore.loadSchools()
        pushSchoolPath('/students')
        break
      case 'add-student':
        await goToAddStudent()
        break
      case 'add-evaluation':
        if (areEvaluationsEnabled()) await goToAddEvaluation()
        break
      case 'print': {
        // Students page intercepts this to open a column-picker dialog.
        const ev = new CustomEvent(PRINT_REQUEST_EVENT, { cancelable: true })
        if (window.dispatchEvent(ev)) window.print()
        break
      }
      case 'admin':
        navigateToAdmin()
        break
      case 'district':
        router.push('/district')
        break
      case 'my-caseload':
        router.push('/my-caseload')
        break
      case 'aide-schedule':
        pushSchoolPath('/aide-schedule')
        break
      case 'view-aide-schedule':
        if (payload) pushSchoolPath(`/aide-schedule/${payload}`)
        break
      case 'master-schedule':
        pushSchoolPath('/aide-schedule-master')
        break
      case 'feedback-manage':
        pushSchoolPath('/feedback-manage')
        break
      case 'meeting-calendar':
        pushSchoolPath('/meeting-calendar')
        break
      case 'my-evaluations':
        if (areEvaluationsEnabled()) {
          pushSchoolPath('/students', { scope: 'evaluations' })
        }
        break
      case 'reference-documents':
        pushSchoolPath('/reference-documents')
        break
      case 'gen-ed-hours':
        pushSchoolPath('/gen-ed-hours')
        break
      case 'caseload-report':
        pushSchoolPath('/caseload-report')
        break
      case 'testing-calendar':
        pushSchoolPath('/testing-calendar')
        break
      case 'testing-accommodations':
        pushSchoolPath('/testing-accommodations')
        break
      case 'testing-attendance':
        pushSchoolPath('/testing-attendance')
        break
      case 'help':
        pushSchoolPath('/help')
        break
      case 'logout':
        await handleLogout()
        break
    }
  }

  return {
    navigateToAdmin,
    handleNavAction,
    handleLogout
  }
}
